import * as React from "react";
import * as R from "ramda";
import classNames from "classnames";

import AqButton from "../../Aq/Button";

import GenLoadingSpinner from '../../Gen/LoadingSpinner/component'

import DocAiAssistantThreadFeedbackModal from "../Modals/AiAssistantThreadFeedback";

import { handleActions } from "../../../helpers/Doc/handleActions";

import { backgroundStyleFromNode } from "../../../helpers/Doc/changes";
import createAiAssistantChatSubscription from "../../../channels/ai_assistant_chat_channel";
import { getNewThread } from "../../../aiAssistant";
import GenModal from '../../Gen/Modal/component';

const MINIMUM_MESSAGE_COUNT = 5

function convertSimpleMarkdown(md: string) {
  // Convert headings
  let html = md.replace(/^### (.*)$/gm, '<h4>$1</h4>');

  // Convert bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Convert line breaks
  html = html.replace(/\n/g, '<br>');

  return html;
}

// Utility function to find last assistant message index (DRY principle)
function findLastAssistantMessageIndex(messages: Array<AiAssistantMessage>, data: AiAssistantMessage): number {
  let lastAssistantWithoutId = -1;

  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      // If data has an id, try to match by id
      if (data.id) {
        if (messages[i].id === data.id) {
          return i; // Found exact ID match
        }
        // Track last assistant without ID as fallback
        if (!messages[i].id && lastAssistantWithoutId === -1) {
          lastAssistantWithoutId = i;
        }
        // Continue searching for ID match
      } else {
        // No id on data - update the last assistant message
        return i;
      }
    }
  }

  // If data has ID but no match found, use last assistant without ID as fallback
  // This handles the case where streaming data has an ID but the placeholder doesn't
  return data.id && lastAssistantWithoutId !== -1 ? lastAssistantWithoutId : -1;
}

interface AiAssistantChatProps extends DocAiAssistantChatType<InlineNode>, BlockNodeAll {}

function AiAssistantChat(props: AiAssistantChatProps) {
  const {
    uid,
    messages = [], // Default to an empty array if no messages are provided
    userContext,
    documentContext,
    displayContext,
    editingContext,
    assistant_id,
    threadId,
    onContinue,
    inactiveBool,
    currentVersion,
  } = props;

  const [content, setContent] = React.useState("");
  const [openFeedbackModal, setOpenFeedbackModal] = React.useState(false)
  const [errorCount, setErrorCount] = React.useState(0);
  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [isSubmittingMessage, setIsSubmittingMessage] = React.useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(false);
  //Track if waiting for a response
  const responseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const threadCreationPromiseRef = React.useRef<Promise<any> | null>(null);
  const streamingCompletionTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Show fallback cards
  const showAiFallbackCards = React.useCallback(() => {
    userContext.addState({
      [currentVersion.rootUid]: {
        error: "aiAssistantChatError",
        aiFallbackCards: true,
      },
    });
  }, [userContext, currentVersion.rootUid]);

    // Centralized error handler
  const handleError = React.useCallback((userMessage?: AiAssistantMessage) => {
    setErrorCount(prev => prev + 1);
    userContext.addState((getStateFunction) => {
      const currentMessages = getStateFunction(uid).messages || [];
      const errorMessage: AiAssistantMessage = {
        content: "Something went wrong. Please try again",
        role: "assistant",
      };

      // Check if last message is an empty assistant placeholder - replace it instead of appending
      const lastMessage = currentMessages[currentMessages.length - 1];
      const shouldReplaceLastMessage = lastMessage?.role === "assistant" && !lastMessage.content?.trim();

      let newMessages;
      if (userMessage) {
        // userMessage not in state yet - add both userMessage and errorMessage
        newMessages = currentMessages.concat(userMessage, errorMessage);
      } else if (shouldReplaceLastMessage) {
        // Replace empty assistant placeholder with error message
        newMessages = R.update(currentMessages.length - 1, errorMessage, currentMessages);
      } else {
        // Append error message
        newMessages = currentMessages.concat(errorMessage);
      }

      return {
        [uid]: {
          messages: newMessages,
        },
      };
    });
  }, [uid, userContext]);

  // Side effects for errorCount
  React.useEffect(() => {
    if (errorCount >= 2) {
      setErrorModalOpen(true);
    }
  }, [errorCount]);

  const onReceived = React.useCallback((data: any) => {
    if (data.status === "error") {
      handleError();
      setIsSubmittingMessage(false);
      setIsStreaming(false);
      return;
    }
    if (data.role !== "assistant") { return }

    // Clear the response timeout as we have received a response
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }

    // Mark as streaming when we receive assistant messages
    setIsStreaming(true);

    // Direct state update - React batches updates automatically
    userContext.addState((getStateFunction) => {
      const currentMessages: Array<AiAssistantMessage> = getStateFunction(uid).messages || [];
      const lastAssistantIndex = findLastAssistantMessageIndex(currentMessages, data);

      const updatedMessages =
        lastAssistantIndex > -1
          ? R.update(lastAssistantIndex, data, currentMessages)
          : currentMessages.concat([data]);

      return {
        [uid]: {
          messages: updatedMessages,
        },
      };
    });

    // Only re-enable submission after streaming appears complete
    // Clear any existing timer and start a new one
    if (streamingCompletionTimerRef.current) {
      clearTimeout(streamingCompletionTimerRef.current);
      streamingCompletionTimerRef.current = null;
    }

    // Use longer timeout for empty messages to give AI time to respond
    const timeout = data.content?.trim() ? 1500 : 30000;

    streamingCompletionTimerRef.current = setTimeout(() => {
      // No new chunks - streaming is complete
      setIsStreaming(false);
      setIsSubmittingMessage(false);
      isMessageSubmittingRef.current = false;

      // Refocus the input field after AI response completes
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);

      // Check if the final message is empty after streaming completes
      userContext.addState((getStateFunction) => {
        const currentMessages: Array<AiAssistantMessage> = getStateFunction(uid).messages || [];
        const lastMessage = currentMessages[currentMessages.length - 1];

        // If last message is from assistant and empty, treat as error
        if (lastMessage?.role === "assistant" && !lastMessage.content?.trim()) {
          setErrorModalOpen(true);
          handleError();
        }

        return {}; // No state change
      });

      streamingCompletionTimerRef.current = null;
    }, timeout);
  }, [handleError, userContext, uid]);

  const subscriptionRef = React.useRef<ReturnType<typeof createAiAssistantChatSubscription>>()
  const isMessageSubmittingRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    // Cleanup on unmount: unsubscribe if subscription exists
    return () => {
      if (subscriptionRef.current && subscriptionRef.current.unsubscribe) {
        subscriptionRef.current.unsubscribe();
      }
      //Clean up timeouts on unmount
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
      if (streamingCompletionTimerRef.current) {
        clearTimeout(streamingCompletionTimerRef.current);
      }
    };
  }, []);

  const isInputDisabled = inactiveBool || isSubmittingMessage || isStreaming || editingContext.isEditing;

  const handleChatSubmission = React.useCallback(async () => {
    if (isInputDisabled || !content.trim()) return;

    // Prevent duplicate submissions during the same request
    if (isMessageSubmittingRef.current) return;
    isMessageSubmittingRef.current = true;

    setIsSubmittingMessage(true);
    setIsStreaming(false);

    const userMessage: AiAssistantMessage = {
      content,
      role: "user",
    }

    // Add user message and empty assistant placeholder to show AI is thinking
    const assistantPlaceholder: AiAssistantMessage = {
      content: "",
      role: "assistant",
    }

    userContext.addState((getStateFunction) => {
      const currentMessages = getStateFunction(uid).messages || [];
      return {
        [uid]: {
          messages: currentMessages.concat(userMessage, assistantPlaceholder),
        },
      };
    });

    setContent("");

    // Blur the input field to prevent focus during AI response
    inputRef.current?.blur();

    try {
      let currentThreadId = threadId

      if (!currentThreadId) {
        // Prevent duplicate thread creation - if already creating, wait for it
        if (threadCreationPromiseRef.current) {
          currentThreadId = (await threadCreationPromiseRef.current).id;
        } else {
          // Start creation
          threadCreationPromiseRef.current = getNewThread(assistant_id, userContext.id, "vpe_chat")
            .then(thread => {
              userContext.addState({
                [uid]: {
                  threadId: thread.id,
                },
              });
              return thread;
            })
            .finally(() => {
              threadCreationPromiseRef.current = null;
            });

          currentThreadId = (await threadCreationPromiseRef.current).id;
        }
      }

      if (!subscriptionRef.current) {
        // Store the message to submit after connection
        const messageToSubmit = userMessage;
        // Track if message has been submitted to prevent duplicate submissions on reconnect
        let messageSubmitted = false;

        subscriptionRef.current = createAiAssistantChatSubscription({
          threadId: `${currentThreadId}`,
          onReceived: onReceived,
          onConnected: () => {
            // Submit the message once connected (for first message)
            // Prevent duplicate submissions if onConnected fires multiple times
            if (subscriptionRef.current && messageToSubmit && !messageSubmitted) {
              messageSubmitted = true;
              subscriptionRef.current.submitChat(messageToSubmit);
            }
          },
          onDisconnected: () => {
            handleError(); // userMessage already in state
            setIsSubmittingMessage(false);
            setIsStreaming(false);
            isMessageSubmittingRef.current = false;
          },
          onRejected: () => {
            handleError(); // userMessage already in state
            setIsSubmittingMessage(false);
            setIsStreaming(false);
            isMessageSubmittingRef.current = false;
          },
        });
      } else if (subscriptionRef.current.isConnected()) {
        subscriptionRef.current.submitChat(userMessage);
      } else {
        subscriptionRef.current = subscriptionRef.current.reconnect(() => {
          subscriptionRef.current.submitChat(userMessage);
        });
      }
      // Start a 30 second response timeout
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
      responseTimeoutRef.current = setTimeout(() => {
        setErrorModalOpen(true);
        setIsSubmittingMessage(false);
        isMessageSubmittingRef.current = false;
      }, 30000);

    }catch (error) {
      // Reset thread creation ref on failure to allow retry
      threadCreationPromiseRef.current = null;
      isMessageSubmittingRef.current = false;

      setIsSubmittingMessage(false);
      // Don't pass userMessage - it's already in state from line 247
      handleError();
    }
  }, [content, uid, userContext, documentContext, assistant_id, threadId, isInputDisabled, handleError, onReceived]);

  const userMessageCount = React.useMemo(
    () => messages.filter((message) => message.role === "user").length,
    [messages]
  );

  const sendBtnDisabled = isInputDisabled || !content.trim();

  return (
    <div
      id={props.uid}
      data-is-node
      data-uid={props.uid}
      className={classNames("ai-assistant-chat", props.uid)}
      style={{...backgroundStyleFromNode(props), marginBottom: "10px"}}
    >
      {displayContext.displayMode !== "student" && (
        <div style={{
          padding: "15px",
          marginBottom: "15px",
          backgroundColor: "#f0f8ff",
          border: "1px solid #99D4EB",
          borderRadius: "4px"
        }}
        contentEditable={false}
        >
          <strong>Note to Educators:</strong> The instructions above are what students see as their instructions for the Virtual Patient Encounter. As an educator, you can see the student experience by asking the patient at least five questions. After submitting a rating on your experience, open the next card in the case, "Feedback - Virtual Patient Encounter," to see your feedback.
        </div>
      )}
      <div style={{ marginBottom: 30, display: "flex", flexDirection: "column", gap: 10}} contentEditable={false}>
        {messages.length === 0 || editingContext.isEditing ? (
          <div
            style={{
              maxWidth: 800,
              padding: "20px",
              color: "#666",
              fontStyle: "italic",
              backgroundColor: "#f9f9f9",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
            }}
          >
            {editingContext.isEditing
              ? "Conversation will be here in view mode"
              : "No messages yet. Start a conversation by typing a message below."
            }
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isLastMessage = idx === messages.length - 1;

            return (
              <div
                key={idx}
                className="ai-message-body"
                style={{
                  maxWidth: 800,
                  marginBottom: "5px",
                  paddingLeft: msg.role === "user" ? 20 : 0,
                  paddingRight: msg.role === "user" ? 0 : 20,
                }}
              >
                <div
                  style={{
                    padding: "10px",
                    border: "1px solid #99D4EB",
                    borderRadius: 8,
                    background: msg.role === "user" ? "#002362" : "#E3F7FF",
                    color: msg.role === "user" ? "white" : "inherit",
                  }}
                >
                  {msg.content || msg.role !== "assistant" || !isLastMessage
                    ? <div className="ai-response-body">
                        <div dangerouslySetInnerHTML={{ __html: convertSimpleMarkdown(msg.content) }} />
                      </div>
                    : <GenLoadingSpinner/>
                  }
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={{ display: "flex", gap: 10, maxWidth: 800, position: "relative" }} contentEditable={false}>
        <input
          ref={inputRef}
          type="text"
          value={content}
          onChange={e => setContent(e.target.value)}
          disabled={isInputDisabled}
          onKeyDown={e => {
                            if (e.key === "Enter" && !isInputDisabled) {handleChatSubmission()}
                          }}
          onFocus={e => {
                            // Prevent focus when disabled
                            if (isInputDisabled) {
                              e.target.blur();
                            }
                          }}
          style={{ flex: 1 }}
          placeholder={isSubmittingMessage || isStreaming ? "AI is responding..." : "Type your message..."}
        />
        <button
          type="button"
          onClick={handleChatSubmission}
          disabled={sendBtnDisabled}
          aria-label="Open chat"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: sendBtnDisabled ? "not-allowed" : "pointer",
            color: sendBtnDisabled ? "#A0A0A0" : "#1A75FF"
          }}
        >
          <i
            className="fa-solid fa-paper-plane"
            style={{
              fontSize: "22px",
              paddingBottom: "2%"
            }}
          />
        </button>
      </div>

      <div contentEditable={false}>
        {!inactiveBool && (
          <AqButton
            onClick={() => {
              if (!isSubmittingFeedback && !editingContext.isEditing) {
                setOpenFeedbackModal(true);
              }
            }}
            disabled={userMessageCount < MINIMUM_MESSAGE_COUNT || isSubmittingFeedback || editingContext.isEditing}
          >
            I DON'T HAVE ANY MORE QUESTIONS
          </AqButton>
        )}
      </div>

      {openFeedbackModal && !editingContext.isEditing && (
        <DocAiAssistantThreadFeedbackModal
          threadId={threadId}
          experienceQuestion="My reaction to this virtual patient chatbot is best expressed as:"
          responseOptions={[
            "All good, patient was realistic",
            "Patient was too easy",
            "Patient was too hard",
            "Slow, unresponsive, or glitchy",
            "Other, please specify below",
          ]}
          continueButtonTitle={"PROCEED TO MY FEEDBACK"}
          onContinue={() => {
            // Double-check we're not in editing mode before executing actions
            if (editingContext.isEditing) {
              setOpenFeedbackModal(false);
              return;
            }
            setOpenFeedbackModal(false);
            if (onContinue) {
              setIsSubmittingFeedback(true);
              handleActions(onContinue, props);
            }
          }}
          onCancel={() => setOpenFeedbackModal(false)}
        />
      )}

      <GenModal open={errorModalOpen}>
        <div className="gen-modal-panel gen-modal-panel-small">
          <h2>AN ERROR HAS OCCURRED</h2>
          <p>Oops! This feature is experiencing difficulty. Sorry for the hassle. You can still complete the case without the Virtual Patient Encounter. Your progress has been saved and you will be directed to the card immediately preceeding the VPE. A record of this error has been logged.</p>
          <p>You can either proceed through the traditional format of this case without the VPE or return later when the error may be resolved.</p>
          <div style={{display: "flex", justifyContent: "center"}}>
            <button className="aq-button-2"onClick={() => {
              setErrorModalOpen(false);
              showAiFallbackCards()
            }}>
              CONTINUE
            </button>
          </div>
        </div>
      </GenModal>
    </div>
  );
}

export default React.memo(AiAssistantChat);
